import { useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Props } from '../types';
import { blokToComponent } from '../helpers';
import { PageStateContext } from '../../services';

type BlacktriangleTextFields = {
  product_sapcode: string;
}

enum RegulatoryTextType {
  MULTIPLE = 'multiple',
  PEI = 'pei',
  BFARM = 'bfarm',
}

const RegulatoryTextMap = {
  multiple: 'dipps_multiple_products_text',
  pei: 'dipps_pei_text',
  bfarm: 'dipps_bfarm_text',
};

// eslint-disable-next-line max-len
const getProductBySapCode = (sapCode, products) => products.find((item) => item.sapCode.toLowerCase() === sapCode.trim().toLowerCase());

const getRegulatoryTextType = (products): RegulatoryTextType | null => {
  if (products.length === 0) return null;
  const uniqueAuthorityNames = products.reduce((accumulator, item) => {
    if (item && item.blackTriangle && !accumulator.includes(item.authorityName)) {
      accumulator.push(item.authorityName);
    }
    return accumulator;
  }, []);
  if (uniqueAuthorityNames.length > 1) {
    return RegulatoryTextType.MULTIPLE;
  }
  return (uniqueAuthorityNames[0] || '').toLowerCase() as RegulatoryTextType;
};

const getRegulatoryTextComponent = (storyblok, textType: RegulatoryTextType) => {
  try {
    return storyblok.SettingsItems.items[0].content[RegulatoryTextMap[textType]];
  } catch {
    return null;
  }
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const PERMANENT_CODE = 'permanent';

export const BlacktriangleText = (
  { blok, getComponent, slot }: Props<BlacktriangleTextFields>,
): JSX.Element => {
  const pageContext = useContext(PageStateContext);
  const productCodes = (blok.product_sapcode || '').split(',');
  const isPermanent: boolean = (blok.product_sapcode || '').trim() === PERMANENT_CODE;

  const products = productCodes.reduce((accumulator, code) => {
    const product = getProductBySapCode(code, pageContext.products);
    if (product) {
      accumulator.push(product);
    }
    return accumulator;
  }, []);

  const { storyblok } = useStaticQuery(graphql`
    {
      storyblok {
        SettingsItems(by_slugs: "global-data/settings") {
          items {
            content {
              dipps_multiple_products_text
              dipps_pei_text
              dipps_bfarm_text
            }
          }
        }
      }
    } 
  `);

  const component = isPermanent
    ? getRegulatoryTextComponent(storyblok, RegulatoryTextType.MULTIPLE)
    : getRegulatoryTextComponent(storyblok, getRegulatoryTextType(products));

  if (!component || !component[0]) {
    return null;
  }

  return blokToComponent({
    blok: component[0],
    getComponent,
    slot,
  });
};
